import { ArrowLeft, ArrowRight } from 'lucide-react';
import { RatingRow } from './RatingRow';

const WEIGHT_LABELS = { 1: 'Nice to Have', 2: 'Important', 3: 'Must Have' };

/**
 * Step 4 — One-feature-at-a-time rating phase.
 * @param {{ feature, featureCount, ratingIdx, weight, vendors, featureRatings, comment, onRate, onSetComment, onNext, onPrev, isLast }} props
 */
export function RatingPhase({ feature, featureCount, ratingIdx, weight, vendors, featureRatings, comment, onRate, onSetComment, onNext, onPrev, isLast }) {
  const progress = ((ratingIdx + 1) / featureCount) * 100;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500">Rating feature {ratingIdx + 1} of {featureCount}</span>
        <span className={`text-xs px-2 py-0.5 rounded font-semibold
          ${weight === 3 ? 'bg-red-900/60 text-red-300' : weight === 2 ? 'bg-amber-900/60 text-amber-300' : 'bg-slate-700 text-slate-400'}`}>
          ×{weight} — {WEIGHT_LABELS[weight]}
        </span>
      </div>
      <div className="w-full h-1.5 bg-slate-700 rounded-full mb-8 overflow-hidden">
        <div className="h-full bg-emerald-600 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-3 mb-3 flex-wrap">
          {feature.is_vendor_sponsored && (
            <span className="text-xs bg-amber-950/60 text-amber-400 border border-amber-800/50 px-2 py-0.5 rounded font-semibold flex-shrink-0">Sponsored</span>
          )}
          <h2 className="text-base md:text-lg font-black text-white leading-tight" data-testid="rating-feature-title">{feature.title}</h2>
        </div>
        <p className="text-slate-400 text-sm leading-relaxed mb-3">{feature.description}</p>
        <p className="text-slate-300 text-sm italic bg-slate-700/50 border-l-2 border-emerald-700 rounded-r-lg px-4 py-3 leading-relaxed">
          "{feature.adventure_text}"
        </p>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 mb-5">
        <p className="text-xs text-slate-400 mb-4 font-medium">Rate each vendor from 1 (poor) to 5 (excellent):</p>
        <div className="divide-y divide-slate-700/50">
          {vendors.map((v, vIdx) => (
            <RatingRow key={vIdx} vendorName={v.name} vendorIdx={vIdx}
              value={featureRatings[vIdx] || 0} onChange={val => onRate(vIdx, val)} />
          ))}
        </div>
      </div>

      <div className="mb-8">
        <label htmlFor="rating-comments" className="text-xs text-slate-400 mb-1.5 block font-medium">
          Notes for this feature (optional)
        </label>
        <textarea id="rating-comments" data-testid="rating-comments" rows={2} value={comment || ''}
          onChange={e => onSetComment(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
          placeholder="Any observations about this feature across the vendors you're comparing…" />
      </div>

      <div className="flex items-center justify-between gap-3">
        <button type="button" data-testid="rating-previous-button" onClick={onPrev} className="text-slate-400 hover:text-white flex items-center gap-2 text-sm transition-colors">
          <ArrowLeft size={16} /> {ratingIdx > 0 ? 'Previous Feature' : 'Back to Competitors'}
        </button>
        <button type="button" data-testid={isLast ? 'see-results' : 'next-feature'} onClick={onNext}
          className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-lg shadow-emerald-900/30">
          {isLast ? 'See My Results' : 'Next Feature'} <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
